import {
  BellOutlined,
  BugOutlined,
  CalendarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  HomeOutlined,
  LeftCircleOutlined,
  MailOutlined,
  ReadOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { useDebounceFn } from 'ahooks'
import { Avatar, Badge, Dropdown, Menu, Popover, Skeleton, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { WebsiteTab } from '@/components'
import { useIsFeatureToggleOn } from '@/components/feature-toggle/feature-toggle.hooks'
import {
  useAppStateQuery,
  useGetViewerQuery,
  useGetWebsitesQuery,
  useUserUpdatedSubscription,
} from '@/generated/graphql'
import { useNotificationUnreadQuery } from '@/modules/notification/use-notification.hook'
import { UserMenu } from '@/modules/user/user-menu'
import { routes } from '@/routes'

import { WebsiteSetCommonContext } from '../app'
import s from './aside.module.less'

const AsideBarItem = ({
  icon,
  path,
  title,
  content,
}: {
  path: string
  icon: React.ReactElement
  title: string | React.ReactElement
  content?: string | React.ReactElement
}) => {
  const history = useHistory()

  const isPopoverString = !!content

  const PopOverComponent = (children: React.ReactElement) =>
    isPopoverString ? (
      <Popover placement='right' title={title} content={content}>
        {children}
      </Popover>
    ) : (
      <Tooltip placement='right' title={title}>
        {children}
      </Tooltip>
    )

  return PopOverComponent(
    <div className={s.barButton} onClick={() => history.push(path)}>
      {icon}
    </div>
  )
}

export const AsideBar = () => {
  const [t] = useTranslation()
  const history = useHistory()
  const { data: appStateData } = useAppStateQuery()
  const [impact, setImpact] = React.useState<number>(0)
  const { unreadCount } = useNotificationUnreadQuery()

  const { toggle } = React.useContext(WebsiteSetCommonContext)

  const isImpactFeatureToggleOn = useIsFeatureToggleOn('impact')
  const isAnniversaryCardFeatureToggleOn = useIsFeatureToggleOn('anniversaryCard')
  const hasLibrary = useIsFeatureToggleOn('knowledgeLibrary')

  const { data, error } = useGetViewerQuery({
    variables: {
      withIsAnniversaryCardCreator: !!isAnniversaryCardFeatureToggleOn,
      withIsAnniversaryCardValidator: !!isAnniversaryCardFeatureToggleOn,
    },
  })

  const { data: websiteData } = useGetWebsitesQuery({
    variables: {
      pagination: {
        last: 1000,
      },
      isCommon: true,
    },
  })

  const websites: { link: string; title: string; avatarSrc?: string }[] = (
    websiteData?.website.entities.edges.slice().map(({ node }) => node) || []
  ).map((website) => ({
    title: website.title,
    link: website.url,
    avatarSrc: website.faviconUrl || undefined,
  }))

  const { run: setShowImpactFalse } = useDebounceFn(() => setImpact(0), { wait: 2000 })

  const viewer = data?.viewer
  const isAnniversaryCardCreator = viewer?.isAnniversaryCardCreator
  const isAnniversaryCardValidator = viewer?.isAnniversaryCardValidator

  useUserUpdatedSubscription({
    skip: !viewer,
    variables: { userId: viewer?.userId || 0 },
    onSubscriptionData({ subscriptionData }) {
      if (subscriptionData.data) {
        const impact = subscriptionData.data.userUpdated.impact
        setImpact(Math.round(impact))
        setShowImpactFalse()
      }
    },
  })

  if (error) {
    return null
  }

  const suggestionMenu = (
    <Menu>
      <Menu.Item icon={<FormOutlined />} onClick={() => history.push(routes.groups.page(23))}>
        {t('sidebar.suggestion')}
      </Menu.Item>
      <Menu.Item icon={<BugOutlined />} onClick={() => history.push(routes.groups.page(6))}>
        {t('sidebar.bugCheck')}
      </Menu.Item>
    </Menu>
  )

  const suggestion = (
    <Dropdown arrow trigger={['hover', 'click']} placement='topRight' overlay={suggestionMenu}>
      <ExclamationCircleOutlined />
    </Dropdown>
  )

  const manageMy = (
    <Tooltip title={'管理群组和用户'}>
      <HomeOutlined onClick={() => history.push(routes.me.manageMy())} />
    </Tooltip>
  )

  return (
    <div className={s.barContainer}>
      <div>
        <AsideBarItem
          title={!appStateData!.appState.showSidebar ? t('home.backToHome') : t('home.title')}
          path={routes.section.updates()}
          icon={
            !appStateData!.appState.showSidebar ? (
              <LeftCircleOutlined style={{ color: '#48bbb5' }} />
            ) : (
              <Avatar
                src='https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/tale-logo-no-padding@2x.png'
                style={{ borderRadius: 0, marginLeft: 4 }}
                size={32}
                shape='square'
              />
            )
          }
        />

        <AsideBarItem
          path={routes.notifications.home()}
          title={t('notification.notification')}
          icon={
            <Badge count={unreadCount}>
              <BellOutlined />
            </Badge>
          }
        />

        {hasLibrary && (
          <AsideBarItem
            title={t('groups.basics.knowledgeLibrary')}
            path={routes.knowledgebase.home()}
            icon={<ReadOutlined />}
          />
        )}
        <AsideBarItem
          title={t('calendar.name')}
          path={routes.calendar.home()}
          icon={
            viewer?.hasUnReadEvent ? (
              <Badge dot offset={[0, 3]}>
                <CalendarOutlined />
              </Badge>
            ) : (
              <CalendarOutlined />
            )
          }
        />
        <AsideBarItem
          content={
            <div style={{ display: 'flex' }}>
              {websites.map((website) => (
                <WebsiteTab
                  key={website.link}
                  avatarSrc={website.avatarSrc}
                  link={website.link}
                  title={website.title}
                />
              ))}
            </div>
          }
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('tools.commonUse')}</span>
              <EditOutlined
                onClick={() => {
                  toggle()
                  history.push(routes.tools.home())
                }}
                style={{ cursor: 'pointer', color: '#48bbb5', fontSize: 16 }}
              />
            </div>
          }
          path={routes.tools.home()}
          icon={<ToolOutlined />}
        />
        {isAnniversaryCardFeatureToggleOn && (isAnniversaryCardCreator || isAnniversaryCardValidator) && (
          <AsideBarItem
            title={t('anniversaryCard.system.title')}
            path={
              isAnniversaryCardValidator
                ? routes.anniversaryCardManagement.validate()
                : routes.anniversaryCardManagement.home()
            }
            icon={<MailOutlined />}
          />
        )}
      </div>

      <div className={s.bottom}>
        {suggestion}
        {manageMy}
        <UserMenu />
        <Badge
          count={isImpactFeatureToggleOn ? impact : 0}
          showZero={false}
          overflowCount={10000000}
          style={{ backgroundColor: 'white', color: '#48bbb5', borderColor: '#48bbb5' }}
        >
          {!viewer ? (
            <Skeleton.Avatar className={s.avatar} size='large' active={true} />
          ) : (
            <Avatar
              onClick={() => history.push(routes.me.home())}
              className={s.avatar}
              size='large'
              src={viewer.avatarUrl}
            />
          )}
        </Badge>
      </div>
    </div>
  )
}
